import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { OPEN_INDOOR_MAP_EDITOR, OpenIndoorMapEditor } from '../../actions/indoorMapEditor';
import { OPEN_OUTDOOR_MAP_EDITOR, OpenOutdoorMapEditor } from '../../actions/outdoorMapEditor';
import {
    FETCH_PREDICTOR_EDITOR,
    FetchPredictorEditorAction,
    initialPredictorReachEditor,
    showPredictorEditorError,
    updatePredictorReachEditor
} from '../../actions/predictorEditor';
import { mapAllPageBlocksToApiFormat, mapPageBlocksToApiFormats } from '../../mappers/predictor';
import {
    selectPageBlocksFromEditorMap,
    selectPredictorAllPageBlocks,
    selectRateCorrectionsDemography
} from '../../selectors/groupEdit/group/group';
import { ApiMethodExecutingError } from '../../service/Errors';
import { EpicDeps } from '../../service/StoreFactory';
import { MapType } from '../../typings/displayMap';
import { State } from '../../typings/state';

const fetchPredictorEditor: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_PREDICTOR_EDITOR),
        mergeMap((action: FetchPredictorEditorAction) => {
            const state = store$.value;
            const allPageBlocks = selectPredictorAllPageBlocks(state, action.mapType);
            const { pageBlocks, groupId } = selectPageBlocksFromEditorMap(state, allPageBlocks || {}, action.mapType);
            const group = state.editGroups.groups[groupId];
            let fetchWithAllPageBlocks;
            let fetchWithCheckPageBlocks;

            if (action.mapType === MapType.outdoor) {
                fetchWithAllPageBlocks = deps.predictorProvider.fetchPredictorOutdoorData(
                    group.campaign.id,
                    groupId,
                    group.isNew,
                    mapAllPageBlocksToApiFormat(allPageBlocks)
                );
                fetchWithCheckPageBlocks = deps.predictorProvider.fetchPredictorOutdoorData(
                    group.campaign.id,
                    groupId,
                    group.isNew,
                    mapPageBlocksToApiFormats(pageBlocks)
                );
            } else {
                const adjustment = selectRateCorrectionsDemography(state, groupId);

                fetchWithAllPageBlocks = deps.predictorProvider.fetchPredictorIndoorData(
                    group.campaign.id,
                    groupId,
                    group.isNew,
                    mapAllPageBlocksToApiFormat(allPageBlocks)
                );

                fetchWithCheckPageBlocks = deps.predictorProvider.fetchPredictorIndoorData(
                    group.campaign.id,
                    groupId,
                    group.isNew,
                    mapPageBlocksToApiFormats(pageBlocks),
                    adjustment
                );
            }

            return forkJoin(fetchWithCheckPageBlocks, fetchWithAllPageBlocks).pipe(
                map(([detailedResult, basicResult]) => {
                    if (!detailedResult || !basicResult) {
                        throw new Error();
                    }

                    const result = {
                        groupId: group.campaign.id,
                        reqId: detailedResult.id,
                        basic: basicResult.data,
                        detailed: detailedResult.data,
                        otsDetailed: detailedResult.otsData,
                        otsBasic: basicResult.otsData
                    };

                    return updatePredictorReachEditor(result);
                }),
                catchError(
                    (error: ApiMethodExecutingError): Observable<Action> => {
                        return of(showPredictorEditorError(error.getNativeError().message));
                    }
                )
            );
        })
    );
};

const initialPredictorOutdoorEditor: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_OUTDOOR_MAP_EDITOR),
        map(
            (action: OpenOutdoorMapEditor): Action => {
                const state = store$.value;
                const predictorValue = state.predictor.data[action.groupId];

                return initialPredictorReachEditor(predictorValue);
            }
        )
    );
};

const initialPredictorIndoorEditor: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_INDOOR_MAP_EDITOR),
        map(
            (action: OpenIndoorMapEditor): Action => {
                const state = store$.value;
                const predictorValue = state.predictor.data[action.groupId];

                return initialPredictorReachEditor(predictorValue);
            }
        )
    );
};

export { fetchPredictorEditor, initialPredictorOutdoorEditor, initialPredictorIndoorEditor };
